import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Waves } from "lucide-react";
import { toast } from "sonner";

export default function History() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/diagnostic/list")
      .then(({ data }) => setItems(data.items))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  if (!items) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12" data-testid="history-page">
      <div className="eyebrow">Historique</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-bold tracking-tight">Tous vos diagnostics</h1>
      <p className="mt-2 text-sm text-muted-foreground">Suivi complet de l'état de votre spa dans le temps.</p>

      <div className="mt-10">
        {items.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Waves className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">Aucun diagnostic pour le moment.</p>
            <Link to="/diagnostic" className="text-primary text-sm font-semibold mt-4 inline-block">Lancer mon premier diagnostic →</Link>
          </Card>
        ) : (
          <Card className="border-black/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-black/5">
                <tr className="text-left">
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Résumé</th>
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Score</th>
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Statut</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const s = d.result?.overall_score ?? 0;
                  const sc = s >= 75 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600";
                  return (
                    <tr key={d.id} className="border-b border-black/5 last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4 text-muted-foreground">
                        {new Date(d.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-6 py-4 font-medium">{d.result?.headline}</td>
                      <td className={`px-6 py-4 font-display font-black text-lg tabular-nums ${sc}`}>{s}</td>
                      <td className="px-6 py-4">
                        {d.paid ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">Débloqué</Badge>
                                : <Badge variant="outline">Aperçu</Badge>}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Link to={`/results/${d.id}`} className="text-primary hover:underline font-semibold text-xs" data-testid={`history-view-${d.id}`}>Voir →</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
